import { brokerService } from 'api/services/broker'
import { BrokerDetails, BrokerUser } from 'api/types/broker'
import { Wrapper } from 'components'
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { ArrowLeft, Trash } from 'phosphor-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { RouteNamesAuth } from 'routes/enum'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { authService } from 'api/services/auth'
import { InviteUserItem } from 'api/types/auth'
import { format } from 'date-fns'

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Email inválido').required('O email é obrigatório'),
})

export const BackofficeBroker = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  //first load and is api being called
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSendingInvite, setIsSendingInvite] = useState<boolean>(false)

  //loaded broker data
  const [data, setData] = useState<BrokerDetails | null>(null)
  const [brokerUsers, setBrokerUsers] = useState<BrokerUser[]>([])
  const [brokerInvites, setBrokerInvites] = useState<InviteUserItem[]>([])

  const getBroker = async () => {
    setIsLoading(true)
    if (!id) return
    try {
      //call api to
      const response = await brokerService.getBroker({
        brokerNo: parseInt(id as string),
      })
      setData(response as BrokerDetails)

      try {
        const invitesResponse = (await authService.getUserInviteList({
          brokerNo: Number(id!),
        })) as InviteUserItem[]
        setBrokerInvites(invitesResponse)
      } catch (error) {
        console.error('Error fetching broker invites:', error)
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do broker.')
      //toast error
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteInvite = async (email: string) => {
    try {
      await authService.deleteInvite({
        email: email,
        brokerNo: Number(id),
      })
      setBrokerInvites(prevInvites =>
        prevInvites.filter(invite => invite.email !== email),
      )
      toast.success('Convite eliminado com sucesso.')
    } catch (error) {
      toast.error('Erro ao eliminar convite.')
    }
  }

  const getBrokerUsers = async () => {
    setIsLoading(true)
    try {
      const response = await brokerService.getBrokerContacts({
        brokerNo: parseInt(id as string),
      })
      setBrokerUsers((response as any).Contact || [])
    } catch (error) {
      //toast error
    } finally {
      setIsLoading(false)
    }
  }

  const handleAccessLevelChange = async (
    contactNo: number,
    newAccessLevel: number,
  ) => {
    try {
      await brokerService.changeBrokerContactAccessLevel({
        brokerNo: Number(id),
        contactNo,
        accessLevel: newAccessLevel,
      })
      setBrokerUsers(prevUsers =>
        prevUsers.map(user =>
          user.ContactNo === contactNo
            ? {
                ...user,
                Access: {
                  ...user.Access,
                  AccessLevel: newAccessLevel.toString(),
                },
              }
            : user,
        ),
      )
      toast.success('Nível de acesso atualizado com sucesso.')
    } catch (error) {
      toast.error('Erro ao atualizar nível de acesso.')
    }
  }

  const enableBrokerContact = async (contactNo: number) => {
    try {
      await brokerService.enableBrokerContact({
        brokerNo: Number(id),
        contactNo,
      })
      setBrokerUsers(prevUsers =>
        prevUsers.map(user =>
          user.ContactNo === contactNo ? { ...user, Active: true } : user,
        ),
      )
      toast.success('Utilizador ativado com sucesso.')
    } catch (error) {
      toast.error('Erro ao ativar utilizador.')
    }
  }

  const disableBrokerContact = async (contactNo: number) => {
    try {
      await brokerService.disableBrokerContact({
        brokerNo: Number(id),
        contactNo,
      })

      setBrokerUsers(
        prevUsers =>
          prevUsers.map(user =>
            user.ContactNo === contactNo ? { ...user, Active: false } : user,
          ) as BrokerUser[],
      )
      toast.success('Utilizador desativado com sucesso.')
    } catch (error) {
      toast.error('Erro ao desativar utilizador.')
    }
  }

  const inviteFormik = useFormik({
    initialValues: { email: '' },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      setIsSendingInvite(true)
      if (!id) return
      try {
        await authService.generateInvite({
          brokerNo: Number(id!),
          email: values.email,
        })
        toast.success('Convite enviado com sucesso.')
        inviteFormik.resetForm()
      } catch (error: any) {
        toast.error(error.response.data.detail)
      } finally {
        setIsSendingInvite(false)
        setShowInviteModal(false)
        getBroker()
      }
    },
  })

  useEffect(() => {
    getBroker()
    getBrokerUsers()
  }, [id])

  return (
    <div className="py-12">
      <Wrapper>
        {isLoading || !data ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Button
              variant="outline"
              color="secondary"
              className="mb-6"
              onClick={() => navigate(RouteNamesAuth.BACKOFFICE_BROKERS)}
            >
              <ArrowLeft size={16} /> Brokers
            </Button>
            <Card className="w-full max-w-full p-4 md:p-6">
              <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
                <span>{data.Broker.Name}</span>
              </h1>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <p className="text-sm">
                    <strong>BrokerNo: </strong> {data.Broker.BrokerNo}
                  </p>
                  <p className="text-sm">
                    <strong>Estado: </strong>{' '}
                    {data.Broker.Active ? 'Ativo' : 'Inativo'}
                  </p>
                  <p className="text-sm">
                    <strong>Segmento: </strong> {data.Broker.Segmentation}
                  </p>
                </div>
                <div className="space-y-4">
                  <p className="text-sm">
                    <strong>Morada: </strong>
                    <br />

                    {data.Broker.CorrespondanceAddress.AddressLine1}
                    <br />
                    {data.Broker.CorrespondanceAddress.AddressLine2}
                    <br />
                    {data.Broker.CorrespondanceAddress.AddressLine3}
                    <br />
                    {data.Broker.CorrespondanceAddress.AddressLine4}
                    <br />
                    {data.Broker.CorrespondanceAddress.Postcode}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="w-full max-w-full p-4 md:p-6 my-6">
              <div className="flex justify-between items-center pb-6">
                <h2 className=" text-xl font-medium text-metal-900 flex items-center justify-between">
                  Utilizadores do Broker
                </h2>
                <Button
                  color="primary"
                  variant="softBg"
                  onClick={() => setShowInviteModal(true)}
                >
                  Convidar Utilizador
                </Button>
              </div>
              {brokerUsers.length === 0 ? (
                <p>Nenhum utilizador encontrado para este broker.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Cargo</TableHead>
                      <TableHead></TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  {brokerUsers.length > 0 && (
                    <TableBody>
                      {brokerUsers.map((user, index) => (
                        <TableRow key={`user__${index}`}>
                          <TableCell>
                            {user.Forename} {user.Surname}
                          </TableCell>
                          <TableCell>{user.Email}</TableCell>

                          <TableCell>
                            {user.Active ? 'Ativo' : 'Inativo'}
                          </TableCell>
                          <TableCell>
                            <Badge
                              color="primary"
                              variant="background"
                              className="whitespace-nowrap"
                            >
                              {user.Access.AccessLevel === '4'
                                ? 'Broker Master'
                                : user.Access.AccessLevel === '3'
                                  ? 'Admin'
                                  : user.Access.AccessLevel === '2'
                                    ? 'Senior'
                                    : user.Access.AccessLevel === '1'
                                      ? 'Junior'
                                      : 'Read Only'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <ButtonGroup>
                              <Button
                                color="secondary"
                                variant="softBg"
                                size="sm"
                                position="start"
                                disabled={user.Access.AccessLevel === '0'}
                                onClick={() =>
                                  handleAccessLevelChange(user.ContactNo, 0)
                                }
                              >
                                Read Only
                              </Button>
                              <Button
                                color="secondary"
                                variant="softBg"
                                size="sm"
                                position="center"
                                disabled={user.Access.AccessLevel === '1'}
                                onClick={() =>
                                  handleAccessLevelChange(user.ContactNo, 1)
                                }
                              >
                                Junior
                              </Button>
                              <Button
                                color="secondary"
                                variant="softBg"
                                size="sm"
                                position="center"
                                disabled={user.Access.AccessLevel === '2'}
                                onClick={() =>
                                  handleAccessLevelChange(user.ContactNo, 2)
                                }
                              >
                                Senior
                              </Button>
                              <Button
                                color="secondary"
                                variant="softBg"
                                size="sm"
                                position="end"
                                disabled={user.Access.AccessLevel === '3'}
                                onClick={() =>
                                  handleAccessLevelChange(user.ContactNo, 3)
                                }
                              >
                                Admin
                              </Button>
                            </ButtonGroup>
                          </TableCell>
                          <TableCell>
                            {user.Active ? (
                              <Button
                                color="primary"
                                variant="softBg"
                                size="sm"
                                onClick={() =>
                                  disableBrokerContact(user.ContactNo)
                                }
                              >
                                Apagar Contacto
                              </Button>
                            ) : (
                              <Button
                                color="primary"
                                variant="softBg"
                                size="sm"
                                onClick={() =>
                                  enableBrokerContact(user.ContactNo)
                                }
                              >
                                Ativar
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
                </Table>
              )}
            </Card>

            <Card className="p-4 w-full max-w-full">
              <h3 className="text-body-2 text-primary-600 pb-6">Convites</h3>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Data Expiração</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brokerInvites.map((invite, index) => (
                    <TableRow key={`invite__${index}`}>
                      <TableCell className="normal-case">
                        {invite.email}
                      </TableCell>
                      <TableCell>
                        {format(new Date(invite.expirationDate), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        <Button
                          color="primary"
                          shape="icon"
                          variant="outline"
                          onClick={() => {
                            handleDeleteInvite(invite.email)
                          }}
                        >
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </Wrapper>

      <Modal
        open={showInviteModal}
        onOpenChange={() => setShowInviteModal(false)}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle className="pb-4">Convidar Utilizador</ModalTitle>
            <ModalDescription className="sr-only hidden">
              Modal para convidar um novo utilizador para o broker.
            </ModalDescription>
            <form onSubmit={inviteFormik.handleSubmit}>
              <div>
                <Input
                  name="email"
                  placeholder="Email"
                  type="email"
                  onChange={inviteFormik.handleChange}
                  className={`${
                    inviteFormik.errors.email ? 'border-error-500' : ''
                  }`}
                />
                {inviteFormik.errors.email && (
                  <p className="text-error-500 text-body-5 mt-1">
                    {inviteFormik.errors.email}
                  </p>
                )}
              </div>
            </form>
          </ModalHeader>
          <ModalFooter>
            <nav className="flex items-center justify-between gap-4 w-full mt-4">
              <Button
                color="secondary"
                variant="outline"
                onClick={() => setShowInviteModal(false)}
                className="w-full"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                className="w-full"
                disabled={isSendingInvite}
                onClick={() => inviteFormik.handleSubmit()}
              >
                {isSendingInvite ? 'Enviando...' : 'Enviar Convite'}
              </Button>
            </nav>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
